/* Calculator inputs stay in this page. No network requests or storage. */
(function (scope) {
  'use strict';
  const limits = { calls: 1000000, answerRate: 100, bookingRate: 100, jobValue: 10000000, monthlyCost: 10000000 };
  function calculate(input) {
    const value = {};
    for (const [key, max] of Object.entries(limits)) {
      const raw = input[key];
      const n = Number(raw);
      if (!['number', 'string'].includes(typeof raw) || String(raw).trim() === '' || !Number.isFinite(n) || n < 0 || n > max || (key === 'calls' && !Number.isInteger(n))) {
        throw new RangeError(key);
      }
      value[key] = n;
    }
    const missedCalls = value.calls * (100 - value.answerRate) / 100;
    const lostBookings = missedCalls * value.bookingRate / 100;
    const totalBookings = value.calls * value.bookingRate / 100;
    const unitCost = denominator => {
      const ratio = denominator > 0 ? value.monthlyCost / denominator : NaN;
      return Number.isFinite(ratio) ? ratio : null;
    };
    return {
      missedCalls, lostBookings, totalBookings,
      lostRevenue: lostBookings * value.jobValue,
      costPerBooking: unitCost(totalBookings),
      costPerRecoveredBooking: unitCost(lostBookings)
    };
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { calculate };
  if (!scope.customElements || scope.customElements.get('omni-cost-calculator')) return;

  const presets = {
    hvac: { calls: 200, answerRate: 70, bookingRate: 40, jobValue: 350 },
    plumbing: { calls: 150, answerRate: 75, bookingRate: 45, jobValue: 250 },
    restaurant: { calls: 400, answerRate: 80, bookingRate: 30, jobValue: 60 },
    dental: { calls: 180, answerRate: 85, bookingRate: 35, jobValue: 200 }
  };
  const messages = {
    calls: 'Enter calls as a whole number from 0 to 1,000,000.',
    answerRate: 'Enter an answer rate from 0 to 100.',
    bookingRate: 'Enter a booking rate from 0 to 100.',
    jobValue: 'Enter a job value from $0 to $10,000,000.',
    monthlyCost: 'Enter a monthly service budget from $0 to $10,000,000.'
  };
  const count = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const unitMoney = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

  class CostCalculator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.error = this.querySelector('[data-error]');
      this.addEventListener('input', (event) => {
        if (!(event.target instanceof HTMLInputElement)) return;
        if (event.target.name !== 'monthlyCost') this.querySelectorAll('[data-preset]').forEach(b => b.setAttribute('aria-pressed', 'false'));
        if (event.target.name === 'monthlyCost') this.querySelector('[data-budget-source]').textContent = 'Custom monthly service budget';
        this.update();
      });
      this.form.addEventListener('submit', (event) => { event.preventDefault(); this.update(true); });
      this.addEventListener('click', (event) => {
        const presetButton = event.target.closest('[data-preset]');
        if (presetButton) {
          const preset = presets[presetButton.dataset.preset];
          if (!preset) return;
          for (const [key, value] of Object.entries(preset)) this.form.elements.namedItem(key).value = value;
          this.querySelectorAll('[data-preset]').forEach(b => b.setAttribute('aria-pressed', String(b === presetButton)));
          this.update();
        }
        const planButton = event.target.closest('[data-plan-price]');
        if (planButton) {
          this.form.elements.namedItem('monthlyCost').value = planButton.dataset.planPrice;
          this.querySelector('[data-budget-source]').textContent = planButton.dataset.planName + ' base price';
          this.update();
          this.form.elements.namedItem('monthlyCost').focus({ preventScroll: true });
          this.querySelector('[data-calculator]').scrollIntoView({ behavior: 'auto', block: 'start' });
        }
      });
      this.update();
    }
    update(focusError = false) {
      const input = {};
      for (const key of Object.keys(limits)) {
        const field = this.form.elements.namedItem(key);
        input[key] = field.value;
        field.removeAttribute('aria-invalid');
      }
      try {
        const result = calculate(input);
        this.error.textContent = '';
        this.querySelector('[data-results]').removeAttribute('data-invalid');
        for (const [key, value] of Object.entries(result)) {
          const output = this.querySelector('[data-output="' + key + '"]');
          if (!output) continue;
          output.textContent = value === null ? 'Not available' : key.startsWith('costPer') ? unitMoney.format(value) : key === 'lostRevenue' ? money.format(value) : count.format(value);
        }
      } catch (error) {
        const key = error.message;
        this.error.textContent = messages[key] || 'Check your inputs.';
        const field = this.form.elements.namedItem(key);
        if (field) { field.setAttribute('aria-invalid', 'true'); if (focusError) field.focus(); }
        this.querySelector('[data-results]').setAttribute('data-invalid', '');
        this.querySelectorAll('[data-output]').forEach(output => { output.textContent = 'Not available'; });
      }
    }
  }
  scope.customElements.define('omni-cost-calculator', CostCalculator);
})(typeof window !== 'undefined' ? window : globalThis);
